import React, { useMemo, useState } from 'react';
import { safeHTML } from '../../../../utils/safeHTML';
import Keyboard from '../../../baseComponents/gui/keyboard/Keyboard';
import CustomButton from '../../../features/customButton/ui/main/CustomButton';
import classNames from 'classnames';
import { useModal } from '../../../../hooks/useModal';
import { nyTaskContent } from '../../../../constants/copyright';

const { afterAnswerModal } = nyTaskContent;

const QuizItemRebus = React.forwardRef(
  ({ text: title, image, answers: [right], nextStep, setScore, note_image, note_title, note }, ref) => {
    const { id, text: rightWords } = right;

    const [text, setText] = useState(() => toFormattedState(rightWords));

    const availableWords = useMemo(() => text.find(({ isActive }) => isActive)?.availableWords, [text]);

    const { addModal } = useModal();

    const setActive = (id) => {
      setText((prev) =>
        prev.map((data, index) => ({
          ...data,
          isActive: index === id,
        })),
      );
    };

    const setActiveText = (callback) => {
      const { currentText, rules } = text.find(({ isActive }) => isActive);

      const formattedText = callback(currentText)?.toLowerCase();

      let result = true;

      for (const key in rules) {
        const necessaryField = formattedText[key];
        if (!necessaryField) continue;
        const necessaryCallback = rules[key];
        result = necessaryCallback(necessaryField);
        if (!result) break;
      }

      if (!result) return;

      setText((prev) => prev.map((data) => (data.isActive ? { ...data, currentText: formattedText } : data)));
    };

    const buttonType = useMemo(() => {
      const types = {
        complete: () =>
          text?.every?.(({ currentText, right }) => {
            return currentText?.length === right?.length;
          }),
        default: () => true,
      };

      let type;

      for (const key in types) {
        const callback = types[key];
        const result = callback?.();
        if (!result) continue;
        type = key;
        break;
      }

      return type;
    }, [text]);

    const submit = () => {
      if (!buttonType) return;
      ({
        complete() {
          const isRight = text.every(({ currentText, right }) => currentText === right);

          const successCallback = () => {
            setScore(isRight && id);
            nextStep();
          };

          addModal({
            type: 'quizStartModal',
            props: {
              ...afterAnswerModal,
              image: note_image,
              title: note_title,
              description: note,
              goToQuiz: successCallback,
              onClose: successCallback,
            },
          });
        },
        default() {
          const activeIndex = text?.findIndex?.(({ isActive }) => isActive);
          const newActiveIndex = Number(activeIndex !== text?.length - 1 && activeIndex + 1);
          setActive(newActiveIndex);
        },
      })[buttonType]?.();
    };

    return (
      <div className={'quiz__item quiz__item_rebus'} ref={ref}>
        <div className={'quiz__item-block'}>
          <div className={'quiz__item-top'}>
            <div className={'quiz__item-image'}>
              <img src={image} alt={'quiz-rebus-image'} />
            </div>
            <h2 className='quiz__item-question'>{safeHTML(title)}</h2>
            <ul className={`quiz__item-words`}>
              {text?.map(({ right, isActive, currentText }, i) => {
                return (
                  <li
                    key={`li-${i}`}
                    className={classNames('quiz__item-word', {
                      'quiz__item-word_active': isActive,
                    })}
                    onClick={() => setActive(i)}
                  >
                    <div className={'quiz__item-word-text'}>{toFormattedWord(right.length, currentText)}</div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className={`quiz__item-controls`}>
            <div className={`quiz__item-keyboard`}>
              <Keyboard setText={setActiveText} availableWords={availableWords} />
            </div>
            <CustomButton
              className={classNames('button_quiz-a quiz__item-button')}
              text={'other.done'}
              disposable={['complete'].includes(buttonType)}
              onClick={submit}
            />
          </div>
        </div>
      </div>
    );
  },
);

function toFormattedState(words) {
  const formattedWords = words?.split('_').map((text) => ({ text }));

  // Создаем общий набор доступных букв из всех слов
  const allAvailableLetters = [...new Set(words.toLowerCase())];

  return Object.values(formattedWords).reduce((acc, { text }, index) => {
    return [
      ...acc,
      {
        right: text.toLowerCase(),
        isActive: !index,
        currentText: '',
        availableWords: allAvailableLetters, // Используем общий набор букв
        rules: {
          length: (length) => {
            const data = { min: 0, max: text.length };
            return length >= data.min && length <= data.max;
          },
        },
      },
    ];
  }, []);
}

function toFormattedWord(length, currentText) {
  return new Array(length).fill(null).reduce((acc, _, index, arr) => {
    acc += currentText[index] ?? `_${index === arr.length - 1 ? '' : ' '}`;
    return acc;
  }, '');
}

export default QuizItemRebus;
QuizItemRebus.displayName = 'QuizItemRebus';
QuizItemRebus.propTypes = {};
